import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAppLocale } from "../../../lib/useAppLocale.js";
import { useI18n } from "../../../lib/useI18n.js";
import { useAutoWidth } from "./hooks.js";

export interface LocPick {
  lat: number;
  lon: number;
}

interface NominatimResult {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
}

interface Props {
  value: string;
  onChange: (v: string) => void;
  onPick: (label: string, coords: LocPick | null) => void;
  /** Element to portal the dropdown into (the overlay root, so the paper CSS
   *  scope + dark-mode vars still apply while escaping the card's stacking). */
  portalEl: HTMLElement | null;
}

/** Location autocomplete backed by Nominatim (OpenStreetMap). Failures and
 *  offline use degrade silently to a plain free-text field. */
export function LocationField({ value, onChange, onPick, portalEl }: Props) {
  const { t } = useI18n();
  const locale = useAppLocale();
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<NominatimResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [hi, setHi] = useState(0);
  const [menuPos, setMenuPos] = useState<{ left: number; top: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Debounced fetch as user types
  useEffect(() => {
    if (!value || value.length < 2) { setResults([]); return; }
    const ctl = new AbortController();
    setLoading(true);
    const tmr = setTimeout(async () => {
      try {
        const r = await fetch(
          "https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&q="
            + encodeURIComponent(value),
          { signal: ctl.signal, headers: { "Accept-Language": locale } },
        );
        const data: unknown = await r.json();
        setResults(Array.isArray(data) ? (data as NominatimResult[]) : []);
        setHi(0);
      } catch (e) {
        if ((e as Error).name !== "AbortError") setResults([]);
      } finally {
        setLoading(false);
      }
    }, 280);
    return () => { clearTimeout(tmr); ctl.abort(); };
  }, [locale, value]);

  // Close on outside click (ignore clicks inside the portaled menu)
  useEffect(() => {
    const f = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest?.(".loc-menu")) return;
      if (wrapRef.current && !wrapRef.current.contains(t)) setOpen(false);
    };
    document.addEventListener("mousedown", f);
    return () => document.removeEventListener("mousedown", f);
  }, []);

  // Track the input's viewport position so the portaled menu can sit above
  // everything (tabs/labels).
  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setMenuPos({ left: r.left + r.width / 2, top: r.bottom + 4 });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, results.length, value]);

  const pick = (r: NominatimResult) => {
    const label = r.display_name.split(",").slice(0, 2).join(",").trim();
    onPick(label, { lat: parseFloat(r.lat), lon: parseFloat(r.lon) });
    setOpen(false);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (open && results.length > 0) pick(results[hi]!);
      return;
    }
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, results.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    if (e.key === "Escape") { e.stopPropagation(); setOpen(false); }
  };

  const inRef = useAutoWidth(value, 62, 280);
  const menuStyle = (pos: { left: number; top: number }) =>
    ({ position: "fixed", left: pos.left, top: pos.top, transform: "translateX(-50%)" }) as const;

  return (
    <div className="loc-wrap" ref={wrapRef}>
      <div className="loc-input">
        <span className="loc-icon" aria-hidden="true">📍</span>
        <input
          ref={inRef}
          type="text"
          value={value}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={t("taskForm.location")}
          autoComplete="off"
          spellCheck="false"
        />
        {loading && <span className="loc-spin" aria-hidden="true" />}
        {value && (
          <button
            type="button"
            className="loc-clear"
            onClick={() => {
              onChange("");
              onPick("", null);
              inRef.current?.focus();
            }}
            aria-label={t("taskForm.clear")}
          >×</button>
        )}
      </div>
      {open && menuPos && portalEl && value.length >= 2 && results.length > 0 &&
        createPortal(
          <div className="loc-menu" role="listbox" style={menuStyle(menuPos)}>
            {results.map((r, i) => {
              const parts = r.display_name.split(",");
              const head = parts[0]!.trim();
              const tail = parts.slice(1, 3).join(",").trim();
              return (
                <div
                  key={r.place_id}
                  className={"loc-opt" + (i === hi ? " is-hi" : "")}
                  onMouseEnter={() => setHi(i)}
                  onMouseDown={(e) => { e.preventDefault(); pick(r); }}
                  role="option"
                  aria-selected={i === hi}
                >
                  <span className="loc-opt-icon">📍</span>
                  <div className="loc-opt-text">
                    <div className="loc-opt-head">{head}</div>
                    {tail && <div className="loc-opt-tail">{tail}</div>}
                  </div>
                </div>
              );
            })}
          </div>,
          portalEl,
        )}
      {open && menuPos && portalEl && value.length >= 2 && !loading && results.length === 0 &&
        createPortal(
          <div className="loc-menu loc-menu-empty" style={menuStyle(menuPos)}>{t("taskForm.noMatches")}</div>,
          portalEl,
        )}
    </div>
  );
}
